import './global.css';

import type { ServerToClientEvents } from '@pin/schemas/overlay';
import gsap from 'gsap';
import { io, type Socket } from 'socket.io-client';
import { PollNotification } from './poll-notification';
import type { SuperMessage } from './super-message';
import { SuperMessageChat } from './super-message-chat';
import { messageFontSize, noPoll } from './url-flags';

const urlParams = new URLSearchParams(window.location.search);

const overlay: SuperMessage = new SuperMessageChat(messageFontSize);

document.body.appendChild(overlay.element);
window.overlay = overlay;
window.gsap = gsap;

// The poll tags: one in the top-left corner when a poll opens in the chat,
// and below it the result a moderator has put on air, which stays until the
// moderator takes it off.
const pollNotification = new PollNotification();
const pollResult = new PollNotification({ holdDuration: null, below: true });
document.body.append(pollNotification.element, pollResult.element);
window.pollNotification = pollNotification;
window.pollResult = pollResult;

const dev = urlParams.get('dev');
if (dev !== null) {
  void import('./dev').then(({ parseDevMode, startDevMode }) =>
    startDevMode(
      { message: overlay, poll: pollNotification, result: pollResult },
      parseDevMode(dev)
    )
  );
}

const socket: Socket<ServerToClientEvents> = io(
  `${import.meta.env.VITE_ADMIN_API_URL}/overlay`,
  {
    path: '/chat',
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 500,
    reconnectionDelayMax: 2000,
    timeout: 3000,
  }
);

let latestOverlayId: string | undefined;
socket.on('PUBLIC', (msg) => {
  switch (msg.type) {
    case 'OVERLAY_ACTIVE': {
      if (msg.overlay?.id === latestOverlayId) return;
      latestOverlayId = msg.overlay?.id;
      void overlay.animate(msg.overlay);
      break;
    }
    case 'OVERLAY_POLL_OPENED': {
      if (noPoll) {
        break;
      }
      void pollNotification.animate({
        variant: 'opened',
        question: msg.question,
      });
      break;
    }
    case 'OVERLAY_POLL_RESULT': {
      // The result on air, or none. Sent on connect and whenever it changes.
      if (noPoll) {
        break;
      }
      void pollResult.animate(
        msg.result == null
          ? null
          : {
              variant: 'result',
              question: msg.result.question,
              alternatives: msg.result.alternatives,
            }
      );
      break;
    }
    case 'OVERLAY_POLL_HIDE': {
      // The live poll was deleted: the opened tag goes away.
      if (noPoll) {
        break;
      }
      void pollNotification.animate(null);
      break;
    }
  }
});
